/** Shared display helpers for order promised times (Phase B). */

import {
  formatDate,
  formatDateTime,
  formatDayMonth,
  formatTime,
  formatTimeRange,
} from '@comforthub/design-system'
import { isCancelledOrder } from './orderState'

export interface OrderTargetInterval {
  startAt?: string | null
  endAt?: string | null
  precision?: string | null
}

export interface OrderTargets {
  prep?: OrderTargetInterval | null
  pickup?: OrderTargetInterval | null
  delivery?: OrderTargetInterval | null
  completionAt?: string | null
}

export interface OrderAppointment {
  start?: string | null
  end?: string | null
}

function tallinnDateKey(iso: string): string | null {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Tallinn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value
  const y = field('year')
  const m = field('month')
  const day = field('day')
  if (!y || !m || !day) return null
  return `${y}-${m}-${day}`
}

/**
 * The order's times on one line: "Pickup 13:30 · Deliver 14:00–14:30".
 *
 * Pickup is the order's pickup time (OrderDto.pickupAt): when it must be ready
 * and the courier or the client comes for it. Without one, the delivery
 * window, else the old prep target.
 * When an appointment exists, skip Pickup/Prep so the visit time is not double-labeled.
 *
 * Never readyBy: on older orders service-core puts the time the order was
 * placed in that field, and a placed time is not a promise to anyone.
 */
export function formatOrderTargetLine(opts: {
  targets?: OrderTargets | null
  /** OrderDto.pickupAt. */
  pickupAt?: string | null
  /** When true, omit Pickup/Prep (Appointment line owns the visit clock). */
  omitPrep?: boolean
}): string | null {
  const delivery = opts.targets?.delivery
  // Dated unless it is today: "Deliver 14:00–14:30", "Deliver 19 Sep, 14:00–14:30".
  const deliverLine = delivery?.startAt ? `Deliver ${formatTimeRange(delivery.startAt, delivery.endAt)}` : null

  if (opts.pickupAt && !opts.omitPrep) {
    const pickupLine = `Pickup ${formatDateTime(opts.pickupAt)}`
    return deliverLine ? `${pickupLine} · ${deliverLine}` : pickupLine
  }
  if (deliverLine) return deliverLine

  if (opts.omitPrep) {
    return null
  }

  const prep = opts.targets?.prep
  const prepAt = prep?.endAt || prep?.startAt
  if (prepAt) {
    return `Prep ${formatDateTime(prepAt)}`
  }
  return null
}

/** Everything orderDueAt and isOrderOverdue read off an order. */
export interface OrderDueFields {
  status?: string | null
  paymentStatus?: string | null
  targets?: OrderTargets | null
  appointment?: OrderAppointment | null
  /** OrderDto.pickupAt: when the order must be ready. */
  pickupAt?: string | null
  /** OrderDto.pickupConfirmed: false while it is only the system's suggestion. */
  pickupConfirmed?: boolean | null
}

function normalStatus(status?: string | null): string {
  return (status || '').trim().toLowerCase().replace(/[\s_-]+/g, '_')
}

/** The order has left the store's hands: ready, with a courier, or done. */
function pastTheStore(status?: string | null): boolean {
  const s = normalStatus(status)
  return s === 'ready_for_pickup' || s === 'courier_assigned' || s === 'completed'
}

/**
 * When the order is due. Until it is ready, that is its pickup time — the
 * store's deadline. After that (or without one), what was promised to the
 * client: the delivery window, else the pickup window, else the booked visit,
 * else the prep target and the completion time. Never readyBy.
 */
export function orderDueAt(order: OrderDueFields): string | null {
  if (order.pickupAt && !pastTheStore(order.status)) return order.pickupAt

  const delivery = order.targets?.delivery
  if (delivery?.endAt || delivery?.startAt) return delivery.endAt || delivery.startAt || null

  const pickup = order.targets?.pickup
  if (pickup?.endAt || pickup?.startAt) return pickup.endAt || pickup.startAt || null

  if (order.appointment?.end) return order.appointment.end

  const prep = order.targets?.prep
  if (prep?.endAt || prep?.startAt) return prep.endAt || prep.startAt || null

  return order.targets?.completionAt || null
}

/**
 * The store still has to confirm the pickup time: until then the courier,
 * FiveStar and the partner are not told it. Never on a finished order.
 */
export function pickupNeedsConfirm(order: OrderDueFields): boolean {
  if (!order.pickupAt || order.pickupConfirmed !== false) return false
  if (isCancelledOrder(order)) return false
  return normalStatus(order.status) !== 'completed'
}

/** Not confirmed and the suggested pickup is within the hour (or past). */
export function pickupConfirmIsUrgent(
  order: OrderDueFields,
  now: Date | number = Date.now(),
  withinMinutes = 60,
): boolean {
  if (!pickupNeedsConfirm(order)) return false
  const at = Date.parse(order.pickupAt || '')
  if (Number.isNaN(at)) return false
  const nowMs = now instanceof Date ? now.getTime() : now
  return at - nowMs <= withinMinutes * 60_000
}

/** Past its due time, still open. A completed or cancelled order is never late. */
export function isOrderOverdue(order: OrderDueFields, now: Date | number = Date.now()): boolean {
  if (isCancelledOrder(order)) return false
  const status = normalStatus(order.status)
  if (status === 'completed') return false
  const due = orderDueAt(order)
  if (!due) return false
  const dueMs = Date.parse(due)
  if (Number.isNaN(dueMs)) return false
  return dueMs < (now instanceof Date ? now.getTime() : now)
}

function nextTallinnDay(key: string): string {
  const [year, month, day] = key.split('-').map(Number)
  const next = new Date(Date.UTC(year, month - 1, day + 1))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`
}

/**
 * A window with its day, always: "Today, 14:00–14:30", "Tomorrow, 14:00–14:30",
 * "Tue 11 Aug, 14:00–14:30", and "Tue 11 Aug 2025, 14:00–14:30" in another year.
 * Staff read these next to an address, where bare times are ambiguous.
 */
export function formatOrderWindow(start?: string | null, end?: string | null): string | null {
  if (!start) return null
  const startKey = tallinnDateKey(start)
  if (!startKey) return null
  const todayKey = tallinnDateKey(new Date().toISOString())
  if (!todayKey) return null

  let day: string
  if (startKey === todayKey) day = 'Today'
  else if (startKey === nextTallinnDay(todayKey)) day = 'Tomorrow'
  else if (startKey.slice(0, 4) === todayKey.slice(0, 4)) day = formatDayMonth(start, { weekday: true })
  else day = formatDate(start, { weekday: true })

  const from = formatTime(start)
  const to = end ? formatTime(end) : null
  return `${day}, ${to && to !== from ? `${from}–${to}` : from}`
}

/** Booked visit window from OrderDto.appointment (core.events). */
export function formatOrderAppointmentLine(
  appointment?: OrderAppointment | null,
): string | null {
  const start = appointment?.start
  const end = appointment?.end
  if (!start || !end) return null
  const startMs = Date.parse(start)
  const endMs = Date.parse(end)
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null
  return `Appointment ${formatTimeRange(start, end)}`
}

/** True when the order has a linked booking window (salon / event slot). */
export function orderHasAppointment(
  appointment?: OrderAppointment | null,
): boolean {
  return Boolean(appointment?.start && appointment?.end)
}

/**
 * Delivery Kanban / list filter: hide calendar-bound visits unless Needs quote
 * still needs merchant attention on the board.
 */
export function orderShowsOnDeliveryBoard(
  order: {
    calendarBound?: boolean | null
    appointment?: OrderAppointment | null
    quote?: { status?: string | null } | null
  },
  hideCalendarBound: boolean,
): boolean {
  if (!hideCalendarBound) return true
  if (order.quote?.status === 'awaiting_quote') return true
  if (order.calendarBound) return false
  if (orderHasAppointment(order.appointment)) return false
  return true
}

/** Deep-link to Calendar on the appointment day, optionally highlighting an order. */
export function calendarHrefForAppointment(
  appointment: OrderAppointment,
  orderId?: string | null,
): string | null {
  if (!appointment.start) return null
  const date = tallinnDateKey(appointment.start)
  if (!date) return null
  const params = new URLSearchParams({ date })
  if (orderId) params.set('orderId', orderId)
  return `/calendar?${params.toString()}`
}
